/**
 * Admin PUDO Stations Management — Release 2 (2026-04-23)
 * /admin/pudo-stations
 *
 * List · Search · Create · View · Edit · Activate/Deactivate · Soft-Delete · Restore
 */
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Loader2, Search, Plus, MapPin, Edit3, Power, Trash2, RotateCcw, ArrowLeft,
  ShieldCheck, CheckCircle2, XCircle, AlertTriangle,
} from "lucide-react";

const JM_PARISHES = [
  "Kingston", "St. Andrew", "Kingston and St. Andrew", "St. Catherine", "Clarendon",
  "Manchester", "St. Elizabeth", "Westmoreland", "Hanover", "St. James", "Trelawny",
  "St. Ann", "St. Mary", "Portland", "St. Thomas",
];

const statusColor = (s) => ({
  active: "bg-emerald-500/15 text-emerald-700",
  inactive: "bg-slate-500/15 text-slate-700",
  pending: "bg-amber-500/15 text-amber-700",
  rejected: "bg-rose-500/15 text-rose-700",
})[s || "inactive"] || "bg-slate-500/15 text-slate-700";

export default function AdminPudoStations() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (includeDeleted) params.set("include_deleted", "true");
      const res = await api.get(`/admin/pudo-stations?${params.toString()}`);
      setRows(res.data.stations || []);
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, includeDeleted]);

  useEffect(() => { load(); }, [load]);

  const onActivate = async (id) => {
    try { await api.post(`/admin/pudo-stations/${id}/activate`); toast.success("Activated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Activate failed"); }
  };
  const onDeactivate = async (id) => {
    try { await api.post(`/admin/pudo-stations/${id}/deactivate`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Deactivate failed"); }
  };
  const onSoftDelete = async (id) => {
    const reason = window.prompt("Reason for deletion (required)?");
    if (!reason || reason.trim().length < 3) { toast.error("Reason is required (≥3 chars)"); return; }
    if (!window.confirm(`Soft-delete this station?\nReason: ${reason}`)) return;
    try { await api.delete(`/admin/pudo-stations/${id}`, { data: { delete_reason: reason } }); toast.success("Soft-deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const onRestore = async (id) => {
    if (!window.confirm("Restore this station? It will return as inactive.")) return;
    try { await api.post(`/admin/pudo-stations/${id}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-pudo-stations-page">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="admin-pudo-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <MapPin className="w-7 h-7 text-emerald-600" />
              PUDO Stations
            </h1>
            <p className="text-slate-500 text-sm">Manage live PUDO centres. Soft-deleted rows are excluded from customer surfaces.</p>
          </div>
          <Button onClick={() => setCreating(true)} data-testid="admin-pudo-create-btn">
            <Plus className="w-4 h-4 mr-1" /> New PUDO
          </Button>
        </div>

        {/* Filter bar */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search name / address / parish" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="admin-pudo-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="admin-pudo-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} data-testid="admin-pudo-include-deleted" />
            Show soft-deleted
          </label>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          {loading ? (
            <div className="p-10 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-slate-400">No PUDO stations match.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Parish</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">GPS</th>
                  <th className="text-left px-4 py-3">Onboarded by</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className={`border-t border-slate-100 hover:bg-slate-50 ${p.deleted_at ? "opacity-60" : ""}`} data-testid={`admin-pudo-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800 cursor-pointer hover:text-emerald-600" onClick={() => setDetail(p)} data-testid={`admin-pudo-name-${p.id}`}>{p.business_name || "(unnamed)"}</div>
                      <div className="text-xs text-slate-500">{p.address || "—"}</div>
                      {p.deleted_at && <Badge className="mt-1 bg-rose-500/15 text-rose-700 text-[10px]">SOFT-DELETED · {p.delete_reason || ""}</Badge>}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{p.parish || "—"}</td>
                    <td className="px-4 py-3"><Badge className={`${statusColor(p.status)} text-xs`}>{p.status || "unknown"}</Badge></td>
                    <td className="px-4 py-3 text-xs">
                      {p.location?.lat != null && p.location?.lng != null
                        ? (<span className="inline-flex items-center gap-1 text-slate-600">{p.geo_verified && <ShieldCheck className="w-3 h-3 text-emerald-500" />}{p.location.lat.toFixed(4)}, {p.location.lng.toFixed(4)}</span>)
                        : (<span className="text-slate-400">no coords</span>)
                      }
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{p.onboarded_by_agent_name || p.onboarded_by_agent_id || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {!p.deleted_at && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid={`admin-pudo-edit-${p.id}`}><Edit3 className="w-3 h-3" /></Button>
                            {p.status === "active" ? (
                              <Button size="sm" variant="outline" onClick={() => onDeactivate(p.id)} data-testid={`admin-pudo-deactivate-${p.id}`}><Power className="w-3 h-3 text-amber-600" /></Button>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => onActivate(p.id)} data-testid={`admin-pudo-activate-${p.id}`}><Power className="w-3 h-3 text-emerald-600" /></Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => onSoftDelete(p.id)} data-testid={`admin-pudo-delete-${p.id}`}><Trash2 className="w-3 h-3 text-rose-600" /></Button>
                          </>
                        )}
                        {p.deleted_at && (
                          <Button size="sm" variant="outline" onClick={() => onRestore(p.id)} data-testid={`admin-pudo-restore-${p.id}`}><RotateCcw className="w-3 h-3 text-sky-600" /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
      {editing && <EditModal row={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {detail && <DetailModal row={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", address: "", parish: "Kingston", latitude: "", longitude: "", owner_phone: "", owner_name: "", owner_email: "" });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, latitude: Number(form.latitude), longitude: Number(form.longitude) };
      if (!payload.name || !payload.address || !payload.parish || isNaN(payload.latitude) || isNaN(payload.longitude)) {
        toast.error("Name, address, parish, latitude, longitude are required"); setSubmitting(false); return;
      }
      await api.post("/admin/pudo-stations", payload);
      toast.success("PUDO station created");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-pudo-create-modal">
        <h3 className="text-xl font-bold mb-4">New PUDO Station</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {["name", "address"].map((k) => (
            <div key={k} className="col-span-2">
              <label className="text-xs text-slate-500">{k}</label>
              <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`admin-pudo-create-${k}`} />
            </div>
          ))}
          <div>
            <label className="text-xs text-slate-500">parish / area</label>
            <Select value={form.parish} onValueChange={(v) => setForm({ ...form, parish: v })}>
              <SelectTrigger data-testid="admin-pudo-create-parish"><SelectValue /></SelectTrigger>
              <SelectContent>{JM_PARISHES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div />
          <div>
            <label className="text-xs text-slate-500">latitude</label>
            <Input value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} data-testid="admin-pudo-create-lat" />
          </div>
          <div>
            <label className="text-xs text-slate-500">longitude</label>
            <Input value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} data-testid="admin-pudo-create-lng" />
          </div>
          <div>
            <label className="text-xs text-slate-500">primary contact (phone)</label>
            <Input value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} data-testid="admin-pudo-create-phone" />
          </div>
          <div>
            <label className="text-xs text-slate-500">owner name (optional)</label>
            <Input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="admin-pudo-create-owner" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-500">owner email (optional)</label>
            <Input value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} data-testid="admin-pudo-create-email" />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="admin-pudo-create-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Create
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditModal({ row, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: row.business_name || "",
    address: row.address || "",
    parish: row.parish || "Kingston",
    latitude: row.location?.lat ?? "",
    longitude: row.location?.lng ?? "",
    owner_phone: row.owner_phone || "",
    owner_name: row.owner_name || "",
    owner_email: row.owner_email || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form };
      payload.latitude = form.latitude === "" ? null : Number(form.latitude);
      payload.longitude = form.longitude === "" ? null : Number(form.longitude);
      await api.patch(`/admin/pudo-stations/${row.id}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-pudo-edit-modal">
        <h3 className="text-xl font-bold mb-4">Edit · {row.business_name}</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2"><label className="text-xs text-slate-500">name</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-pudo-edit-name" /></div>
          <div className="col-span-2"><label className="text-xs text-slate-500">address</label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="admin-pudo-edit-address" /></div>
          <div>
            <label className="text-xs text-slate-500">parish</label>
            <Select value={form.parish} onValueChange={(v) => setForm({ ...form, parish: v })}>
              <SelectTrigger data-testid="admin-pudo-edit-parish"><SelectValue /></SelectTrigger>
              <SelectContent>{JM_PARISHES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div />
          <div><label className="text-xs text-slate-500">latitude</label><Input value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} data-testid="admin-pudo-edit-lat" /></div>
          <div><label className="text-xs text-slate-500">longitude</label><Input value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} data-testid="admin-pudo-edit-lng" /></div>
          <div><label className="text-xs text-slate-500">owner phone</label><Input value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} data-testid="admin-pudo-edit-phone" /></div>
          <div><label className="text-xs text-slate-500">owner name</label><Input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="admin-pudo-edit-owner" /></div>
          <div className="col-span-2"><label className="text-xs text-slate-500">owner email</label><Input value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} data-testid="admin-pudo-edit-email" /></div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="admin-pudo-edit-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function DetailModal({ row, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-pudo-detail-modal">
        <h3 className="text-xl font-bold mb-1">{row.business_name}</h3>
        <p className="text-sm text-slate-500 mb-4">{row.address || "—"} · {row.parish}</p>
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div><dt className="text-xs text-slate-400">ID</dt><dd className="font-mono text-xs">{row.id}</dd></div>
          <div><dt className="text-xs text-slate-400">Status</dt><dd><Badge className={statusColor(row.status)}>{row.status}</Badge></dd></div>
          <div><dt className="text-xs text-slate-400">GPS</dt><dd className="text-xs">{row.location?.lat}, {row.location?.lng}</dd></div>
          <div><dt className="text-xs text-slate-400">GPS Verified</dt><dd>{row.geo_verified ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-slate-400" />}</dd></div>
          <div><dt className="text-xs text-slate-400">Owner</dt><dd className="text-xs">{row.owner_name || "—"} {row.owner_phone ? `· ${row.owner_phone}` : ""}</dd></div>
          <div><dt className="text-xs text-slate-400">Onboarded by</dt><dd className="text-xs">{row.onboarded_by_agent_name || row.onboarded_by_agent_id || "—"}</dd></div>
          <div className="col-span-2"><dt className="text-xs text-slate-400">Created</dt><dd className="text-xs">{row.created_at || "—"}</dd></div>
          {row.deleted_at && (
            <div className="col-span-2 bg-rose-50 border border-rose-200 rounded p-2">
              <dt className="text-xs text-rose-700 font-semibold flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Soft-Deleted</dt>
              <dd className="text-xs text-rose-700 mt-1">at {row.deleted_at} · reason: {row.delete_reason}</dd>
            </div>
          )}
        </dl>
        <div className="mt-6 flex justify-end"><Button onClick={onClose}>Close</Button></div>
      </div>
    </div>
  );
}
